import classNames from 'classnames';
import React, { useContext, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocale } from '../../contexts/LocaleContext';
import CheckoutContext from '../../lib/CheckoutContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import { giftableTrackers } from '../../lib/hardwareFirstGiftCart';
import { isAmountGiftCardSku } from '../../lib/hardwareFirstGiftPlans';
import { getLocalizedPricing } from '../../lib/util/money';
import { cartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import SimpleDismissableModal from '../modals/SimpleDismissableModal';
import optionStyles from '../StripeCheckout/GiftOptions.module.scss';
import styles from './GiftCheckbox.module.scss';
import modalStyles from './GiftSheet.module.scss';

/** the second tile, $100 in usd: the prototype's pick, a middle amount rather than the cheapest */
const DEFAULT_TILE_INDEX = 1;

interface GiftCardSheetProps {
  trigger: JSX.Element;
}

export default function GiftCardSheet({ trigger }: GiftCardSheetProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const formatters = useLocale();
  const { currency } = formatters;
  const amounts = useMemo(
    () =>
      products
        .flatMap((product) =>
          'sku' in product && isAmountGiftCardSku(product.sku)
            ? [{ sku: product.sku, cents: getLocalizedPricing(product.pricingInCents, currency) }]
            : [],
        )
        .sort((a, b) => a.cents - b.cents),
    [products, currency],
  );
  const [picked, setPicked] = useState<string>();
  const radioName = useId();
  const selected = picked ?? amounts[DEFAULT_TILE_INDEX]?.sku;
  const trackerIds = giftableTrackers(cart, products).map((tracker) => tracker.cartItemId);

  return (
    <SimpleDismissableModal
      trigger={trigger}
      containerClassName={modalStyles.modal}
      closeIconClassName={modalStyles.closeIcon}
    >
      {(close) => (
        <div className={modalStyles.container}>
          <span aria-hidden="true" className={modalStyles.handle} />
          <h2 className={modalStyles.title}>{t('hardware_first_gift_card_sheet_title')}</h2>
          <p className={modalStyles.subtitle}>{t('hardware_first_gift_card_sheet_lede')}</p>
          <div aria-label={t('hardware_first_gift_card_sheet_title')} className={styles.amounts} role="radiogroup">
            {amounts.map(({ sku, cents }) => (
              <label className={classNames(styles.amount, { [optionStyles.on]: selected === sku })} key={sku}>
                <input
                  checked={selected === sku}
                  className={optionStyles.radioInput}
                  name={radioName}
                  onChange={() => setPicked(sku)}
                  type="radio"
                />
                {formatHardwarePrice(cents, formatters)}
              </label>
            ))}
          </div>
          <p className={modalStyles.fine}>{t('hardware_first_gift_card_sheet_currency', { currency })}</p>
          <button
            className={classNames(modalStyles.cta, styles.darkCta)}
            disabled={!selected}
            onClick={() => {
              if (selected) {
                dispatch(cartActions.addAmountGiftCard({ sku: selected, currency, trackerIds }));
              }
              close();
            }}
            type="button"
          >
            {t(trackerIds.length > 0 ? 'hardware_first_gift_card_sheet_replace' : 'hardware_first_gift_card_sheet_add')}
          </button>
        </div>
      )}
    </SimpleDismissableModal>
  );
}
